/**
 * components/client-detail/DossierMainContent.tsx
 *
 * Central scrollable panel of the dossier builder (3-column layout).
 * Renders the sortable included-pieces list and the failed-conversion section.
 * Document preview (DocumentPreviewModal) is triggered via onPreviewDoc and
 * rendered at the parent level (DossierBuilderModal).
 *
 * Replaces DossierStep1 (Apr 2026): sidebars (catalog left, actions right) are
 * now managed at the DossierBuilderModal level. This component is the center column only.
 * Updated Apr 2026: added onReorderDocs and onMoveDoc for per-file reordering within pieces.
 * Updated Jun 2026: added availableFields, onReclassify, onRename for document
 *   reclassification and renaming within the dossier builder.
 */

import { AlertCircle, FolderOpen, RefreshCw, Loader2 } from 'lucide-react';
import DossierPieceList from './DossierPieceList';
import type { DossierPiece, ExcludedDocItem, DocumentEntry } from '../../types/dossier';
import type { AvailableField } from './ClientDocumentsTab';

interface DossierMainContentProps {
  pieces: DossierPiece[];
  includedPieces: DossierPiece[];
  availableFields: AvailableField[];
  failedDocs: ExcludedDocItem[];
  retryingDocIds: Set<string>;
  onLabelChange: (id: string, label: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onReorder: (orderedIds: string[]) => void;
  onExcludeDoc: (docId: string) => void;
  onPreviewDoc: (doc: DocumentEntry) => void;
  onRetry: (docId: string, requestId: string) => void;
  onReorderDocs: (pieceId: string, orderedDocIds: string[]) => void;
  onMoveDoc: (pieceId: string, docId: string, direction: 'up' | 'down') => void;
  onReclassify: (docId: string, targetFieldId: string) => Promise<boolean>;
  onRename: (docId: string, newName: string) => Promise<boolean>;
}

export default function DossierMainContent({
  pieces,
  includedPieces,
  availableFields,
  failedDocs,
  retryingDocIds,
  onLabelChange,
  onMove,
  onReorder,
  onExcludeDoc,
  onPreviewDoc,
  onRetry,
  onReorderDocs,
  onMoveDoc,
  onReclassify,
  onRename,
}: DossierMainContentProps) {
  const hasFailed = failedDocs.length > 0;

  if (pieces.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-neutral-200 shadow-soft max-w-md w-full">
          <FolderOpen className="w-10 h-10 text-neutral-300 mb-3" />
          <p className="text-sm text-neutral-500">Aucune pièce disponible</p>
          <p className="text-xs text-neutral-400 mt-1">
            Les documents convertis en PDF apparaîtront ici
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {includedPieces.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center bg-white rounded-xl border-2 border-dashed border-neutral-200">
            <FolderOpen className="w-8 h-8 text-neutral-300 mb-2" />
            <p className="text-sm text-neutral-400">Aucune pièce sélectionnée</p>
            <p className="text-xs text-neutral-400 mt-1">
              Ajoutez des documents depuis le panneau de gauche
            </p>
          </div>
        ) : (
          <DossierPieceList
            includedPieces={includedPieces}
            availableFields={availableFields}
            onLabelChange={onLabelChange}
            onMove={onMove}
            onReorder={onReorder}
            onExcludeDoc={onExcludeDoc}
            onPreviewDoc={onPreviewDoc}
            onReorderDocs={onReorderDocs}
            onMoveDoc={onMoveDoc}
            onReclassify={onReclassify}
            onRename={onRename}
          />
        )}

        {hasFailed && (
          <div className="bg-error-50 border border-error-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-error-200">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-error-500 shrink-0" />
                <span className="text-xs font-semibold text-error-700 uppercase tracking-wide">
                  Conversion échouée ({failedDocs.length})
                </span>
              </div>
              {failedDocs.length > 1 && (
                <button
                  type="button"
                  onClick={async () => {
                    for (const item of failedDocs) {
                      await onRetry(item.doc.id, item.doc.requestId);
                    }
                  }}
                  disabled={retryingDocIds.size > 0}
                  className="flex items-center gap-1.5 text-xs font-medium text-error-600 hover:text-error-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Tout réessayer
                </button>
              )}
            </div>
            <div className="divide-y divide-error-100">
              {failedDocs.map((item) => {
                const isRetrying = retryingDocIds.has(item.doc.id);
                const name = item.doc.displayFilename ?? item.doc.originalFilename;
                const shortName = name.length > 44 ? `${name.slice(0, 41)}…` : name;
                return (
                  <div key={item.doc.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-error-800 truncate" title={name}>
                        {shortName}
                      </p>
                      <p className="text-xs text-error-500 mt-0.5 truncate">
                        {item.pieceLabel}
                        {item.doc.conversionError && (
                          <span className="ml-1 text-error-400">- {item.doc.conversionError}</span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRetry(item.doc.id, item.doc.requestId)}
                      disabled={isRetrying}
                      className="flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium bg-white border border-error-300 text-error-700 hover:bg-error-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isRetrying ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                      Réessayer
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
